import React from 'react';
import { Box, Grid, Stack, styled, Typography } from '@mui/material';
import PolarisIcon, { PolarisIconProps } from '@/components/_common/PolarisIcon';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';

interface Props {
  primaryText: React.ReactNode;
  secondaryText: React.ReactNode;
  color?: string;
  iconName: PolarisIconProps['icon'];
  iconBgColor?: string;
  bgColor?: string;
  endSlot?: React.ReactNode;
  children?: React.ReactNode;
  onHeaderClick?: () => void;
}

const ScheduleDetailsGridTile: React.FC<Props> = ({
  primaryText,
  secondaryText,
  color = 'tertiary.main',
  iconName,
  iconBgColor,
  bgColor,
  endSlot,
  children,
  onHeaderClick
}) => {
  const theme = useFocusTheme();
  iconBgColor = iconBgColor ?? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');
  bgColor = bgColor ?? (theme.palette.mode === 'dark' ? '#27253B' : '#cfccff');

  return (
    <Grid item xs={12} md={4} lg={3}>
      <DetailsGridTileWrapper
        sx={{
          backgroundColor: bgColor,
          flexGrow: 1
        }}
      >
        <Stack
          direction="row"
          gap={2}
          sx={{
            p: 2,
            alignItems: 'center',
            cursor: onHeaderClick ? 'pointer' : undefined
          }}
          onClick={onHeaderClick}
        >
          <ReadinessIndicator bgcolor={color} />
          <TileIconContainer color={color} bgcolor={iconBgColor}>
            <PolarisIcon icon={iconName} width={24} height={24} style={{ display: 'block' }} />
          </TileIconContainer>
          <Box flexGrow={1} minWidth={0}>
            <Typography component="div" sx={{ fontWeight: 700, fontSize: '1.143rem', lineHeight: '1.5rem' }}>
              {primaryText}
            </Typography>
            <Typography component="div" sx={{ fontSize: '1rem', lineHeight: '1.571rem', color: theme.palette.neutral[20] }}>
              {secondaryText}
            </Typography>
          </Box>
          {endSlot && <Box>{endSlot}</Box>}
        </Stack>
      </DetailsGridTileWrapper>
      {children}
    </Grid>
  );
};

const DetailsGridTileWrapper = styled(Box)`
  min-width: 0; // To make sure that element shrink when there are "white-space: nowrap" children in it
  border-radius: ${({ theme }) => `${theme.shape.borderRadius * 1.2}px`};
`;

const readinessIndicatorWidth = 6;
const ReadinessIndicator = styled(Box)`
  width: ${readinessIndicatorWidth}px;
  align-self: stretch;
  border-radius: ${readinessIndicatorWidth / 2}px;
`;

const TileIconContainer = styled(Box)`
  padding: ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
`;

export default ScheduleDetailsGridTile;
